import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { checkPermission, hasAdminAccess, permissionType } from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import ConfirmModal from "../../atoms/ConfirmModal/ConfirmModal";
import {
  COMPANY_DETAILS_INITIAL_FORM,
  formatCompanyDetailsField,
  validateCompanyDetailsForm,
} from "../../../app/utils/company/companyDetailsFormUtils";
import { COMPANY_PROFILES_UPDATED_EVENT } from "../../../app/utils/company/companyProfilesStorage";
import {
  createCompanyProfileApi,
  deleteCompanyProfileApi,
  fetchCompanyProfiles,
  updateCompanyProfileApi,
} from "../../../app/utils/billing/companyDetailsApi";

const initialForm = COMPANY_DETAILS_INITIAL_FORM;

const CompanyDetailsPage = () => {
  const { user } = useSelector((state) => state.auth.auth);
  const [form, setForm] = useState(initialForm);
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const canCreateCompany =
    hasAdminAccess(user) || checkPermission(permissionType.COMPANY_DETAILS, "create");
  const canUpdateCompany =
    hasAdminAccess(user) || checkPermission(permissionType.COMPANY_DETAILS, "update");
  const canDeleteCompany =
    hasAdminAccess(user) || checkPermission(permissionType.COMPANY_DETAILS, "delete");

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const list = await fetchCompanyProfiles();
      setCompanies(list);
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Unable to load company profiles.");
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
    const onUpdated = () => loadCompanies();
    window.addEventListener(COMPANY_PROFILES_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(COMPANY_PROFILES_UPDATED_EVENT, onUpdated);
  }, []);

  const bankFieldsEntered = useMemo(
    () =>
      Boolean(
        form.bankName ||
          form.accountHolderName ||
          form.accountNumber ||
          form.ifscCode ||
          form.branchName
      ),
    [form]
  );

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: formatCompanyDetailsField(key, value) }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const openCreateForm = () => {
    setForm(initialForm);
    setErrors({});
    setEditingCompanyId(null);
    setShowForm(true);
  };

  const openEditForm = (company) => {
    if (!canUpdateCompany) {
      showErrorToast("You do not have permission to edit company details.");
      return;
    }
    setForm({ ...initialForm, ...company });
    setErrors({});
    setEditingCompanyId(company.id);
    setShowForm(true);
  };

  const closeCreateForm = () => {
    setForm(initialForm);
    setErrors({});
    setEditingCompanyId(null);
    setShowForm(false);
  };

  const validateForm = () => {
    const nextErrors = validateCompanyDetailsForm(form);
    setErrors(nextErrors);
    return nextErrors;
  };

  const onSignatureChange = (event) => {
    const file = event.target.files?.[0];
    setErrors((current) => ({ ...current, signatureFileName: "" }));

    if (!file) {
      updateField("signatureFileName", "");
      updateField("signatureDataUrl", "");
      return;
    }

    const allowedType = !file.type || ["image/jpeg", "image/jpg", "image/png"].includes(file.type);
    const allowedExtension = /\.(jpe?g|png)$/i.test(file.name);

    if (!allowedType || !allowedExtension) {
      setErrors((current) => ({
        ...current,
        signatureFileName: "Signature Photo Upload must be jpg, jpeg, or png.",
      }));
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setForm((current) => ({
        ...current,
        signatureFileName: file.name,
        signatureDataUrl: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!editingCompanyId && !canCreateCompany) {
      showErrorToast("You do not have permission to create company details.");
      return;
    }
    if (editingCompanyId && !canUpdateCompany) {
      showErrorToast("You do not have permission to edit company details.");
      return;
    }

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length) {
      const firstError = Object.values(validationErrors)[0];
      showErrorToast(firstError);
      return;
    }

    setSaving(true);
    try {
      if (editingCompanyId) {
        await updateCompanyProfileApi(editingCompanyId, form);
        showSuccessToast("Company profile updated successfully.");
      } else {
        await createCompanyProfileApi(form);
        showSuccessToast("Company profile created successfully.");
      }
      await loadCompanies();
      closeCreateForm();
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Unable to save company profile.");
    } finally {
      setSaving(false);
    }
  };

  const requestDeleteCompany = (company) => {
    if (!canDeleteCompany) {
      showErrorToast("You do not have permission to delete company details.");
      return;
    }
    setCompanyToDelete(company);
  };

  const confirmDeleteCompany = async () => {
    if (!companyToDelete) return;
    try {
      await deleteCompanyProfileApi(companyToDelete.id);
      if (String(editingCompanyId) === String(companyToDelete.id)) {
        closeCreateForm();
      }
      setCompanyToDelete(null);
      await loadCompanies();
      showSuccessToast("Company profile deleted successfully.");
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Unable to delete company profile.");
    }
  };

  const renderInput = (key, label, placeholder, options = {}) => (
    <div className={options.fullWidth ? "md:col-span-2" : ""}>
      <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
        {label}
        {options.required ? <span className="text-red-500"> *</span> : null}
      </label>
      <input
        type={options.type || "text"}
        value={form[key]}
        onChange={(e) => updateField(key, e.target.value)}
        placeholder={placeholder}
        maxLength={options.maxLength}
        inputMode={options.inputMode}
        className={`search-input w-full !text-black ${errors[key] ? "!border-red-400" : ""}`}
      />
      {errors[key] ? <p className="mt-1 text-xs text-red-500">{errors[key]}</p> : null}
    </div>
  );

  return (
    <div className="company-details-page">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="page-title">Company Details</h1>
          <p className="page-subtitle">
            Manage company profiles used for billing, tax, and official documents.
          </p>
        </div>
        {canCreateCompany && !showForm ? (
          <button
            type="button"
            onClick={openCreateForm}
            className="button bg-btn-primary text-white px-6 py-2 rounded-lg self-start"
          >
            Create Company
          </button>
        ) : null}
      </div>

      {showForm ? (
      <form onSubmit={handleSubmit} className="card-border card-body space-y-8 mb-6">
        <section>
          <h2 className="text-lg font-semibold text-[#27314a] mb-4">
            {editingCompanyId ? "Edit Company" : "Create Company"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {renderInput("companyName", "Company Name", "ABC Pvt Ltd", { required: true })}
            {renderInput("ownerName", "Owner Name", "Himanshu Goswami", { required: true })}
            {renderInput("gstNumber", "GST Number", "22AAAAA0000A1Z5", { required: true })}
            {renderInput("panNumber", "PAN Number", "ABCDE1234F", { required: true })}
            {renderInput("hsnCode", "HSN Code", "Optional")}
            {renderInput("contactNumber", "Contact Number", "9876543210", {
              required: true,
              maxLength: 10,
              inputMode: "numeric",
            })}
            {renderInput("emailId", "Email ID", "admin@abc.com, billing@abc.com", {
              required: true,
            })}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
                Company Address <span className="text-red-500">*</span>
              </label>
              <textarea
                value={form.companyAddress}
                onChange={(e) => updateField("companyAddress", e.target.value)}
                placeholder="Mumbai, India"
                rows={4}
                className={`search-input w-full !h-auto !text-black !border !border-[#d7deed] !rounded-lg ${
                  errors.companyAddress ? "!border-red-400" : ""
                }`}
              />
              {errors.companyAddress ? (
                <p className="mt-1 text-xs text-red-500">{errors.companyAddress}</p>
              ) : null}
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-[#27314a] mb-4">Bank Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {renderInput("bankName", "Bank Name", "State Bank of India")}
            {renderInput("accountHolderName", "Account Holder Name", "ABC Pvt Ltd")}
            {renderInput("accountNumber", "Account Number", "1234567890")}
            {renderInput("ifscCode", "IFSC Code", "SBIN0001234")}
            {renderInput("branchName", "Branch Name", "Mumbai Main Branch", { fullWidth: true })}
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-[#27314a] mb-4">Additional</h2>
          <div className="rounded-xl border border-[#e6ebf6] bg-[#f9fbff] p-4">
            <label className="block text-sm font-medium text-[#5f6d8a] mb-2">
              Signature Photo Upload
            </label>
            <input type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" onChange={onSignatureChange} />
            <p className="mt-2 text-xs text-[#6f7d97]">Allowed formats: jpg, jpeg, png.</p>
            {errors.signatureFileName ? (
              <p className="mt-1 text-xs text-red-500">{errors.signatureFileName}</p>
            ) : null}
            {form.signatureDataUrl ? (
              <div className="mt-4 flex items-center gap-4">
                <img
                  src={form.signatureDataUrl}
                  alt="Signature preview"
                  className="h-16 max-w-[220px] object-contain rounded border border-[#e6ebf6] bg-white p-2"
                />
                <span className="text-sm text-[#5f6d8a]">{form.signatureFileName}</span>
              </div>
            ) : null}
          </div>
        </section>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={closeCreateForm}
            className="button border border-[#d7deed] bg-white text-[#27314a] px-6 py-2 rounded-lg"
          >
            Cancel
          </button>
          <button type="submit" className="button bg-btn-primary text-white px-6 py-2 rounded-lg">
            {editingCompanyId ? "Update Company Profile" : "Create Company Profile"}
          </button>
        </div>
      </form>
      ) : null}

      {!showForm ? (
      <section className="card-border card-body">
        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[#27314a]">Companies</h2>
            <p className="text-sm text-[#6f7d97]">
              {companies.length ? `${companies.length} company profile${companies.length > 1 ? "s" : ""} saved.` : "No company profiles created yet."}
            </p>
          </div>
        </div>

        {companies.length ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {companies.map((company) => (
              <article
                key={company.id || `${company.gstNumber}-${company.panNumber}`}
                className="rounded-xl border border-[#e6ebf6] bg-[#f9fbff] p-5"
              >
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-[#27314a]">
                      {company.companyName}
                    </h3>
                    <p className="text-xs text-[#6f7d97]">{company.emailId}</p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    {company.signatureDataUrl ? (
                      <img
                        src={company.signatureDataUrl}
                        alt={`${company.companyName} signature`}
                        className="h-12 max-w-[140px] object-contain rounded border border-[#e6ebf6] bg-white p-1"
                      />
                    ) : null}
                    {(canUpdateCompany || canDeleteCompany) ? (
                      <div className="flex flex-wrap justify-end gap-2">
                        {canUpdateCompany ? (
                          <button
                            type="button"
                            onClick={() => openEditForm(company)}
                            className="rounded-md border border-[#d7deed] bg-white px-3 py-1 text-xs font-medium text-[#27314a]"
                          >
                            Edit
                          </button>
                        ) : null}
                        {canDeleteCompany ? (
                          <button
                            type="button"
                            onClick={() => requestDeleteCompany(company)}
                            className="rounded-md border border-red-200 bg-white px-3 py-1 text-xs font-medium text-red-600"
                          >
                            Delete
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-[#5f6d8a]">
                  <p>
                    <span className="font-medium text-[#27314a]">GST:</span> {company.gstNumber}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">Owner:</span> {company.ownerName || "-"}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">PAN:</span> {company.panNumber}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">Contact:</span> {company.contactNumber}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">HSN:</span> {company.hsnCode || "-"}
                  </p>
                  <p className="sm:col-span-2">
                    <span className="font-medium text-[#27314a]">Address:</span> {company.companyAddress}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">Bank:</span> {company.bankName || "-"}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">Account Holder:</span>{" "}
                    {company.accountHolderName || "-"}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">Account Number:</span>{" "}
                    {company.accountNumber || "-"}
                  </p>
                  <p>
                    <span className="font-medium text-[#27314a]">IFSC:</span> {company.ifscCode || "-"}
                  </p>
                  <p className="sm:col-span-2">
                    <span className="font-medium text-[#27314a]">Branch:</span> {company.branchName || "-"}
                  </p>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-[#d7deed] bg-[#f9fbff] p-8 text-center">
            <h3 className="text-base font-semibold text-[#27314a] mb-1">No companies found</h3>
            <p className="text-sm text-[#6f7d97]">
              Use the Create Company button to add your first company profile.
            </p>
          </div>
        )}
      </section>
      ) : null}
      <ConfirmModal
        isOpen={Boolean(companyToDelete)}
        onClose={() => setCompanyToDelete(null)}
        onConfirm={confirmDeleteCompany}
        title="Delete Company"
        content={`Are you sure you want to delete ${companyToDelete?.companyName || "this company"}?`}
      />
    </div>
  );
};

export default CompanyDetailsPage;
